/**
 * Neyra — Booking + Escrow + Review + Dispute store.
 *
 * Pure localStorage. Indexed by buyer and seller for fast lookups.
 *
 * Booking state machine:
 *   pending_payment → pending (paid, funds in seller escrow)
 *     → completed (buyer confirmed) → released (funds moved to seller available)
 *     → disputed (buyer reported issue) — funds stay in escrow
 *     → cancelled (before payment)
 */

import {
    addPendingFunds,
    addRefund,
    releaseFunds,
} from "@/lib/monetizationStore";
import { addNotification } from "@/lib/socialStore";
import { rewardIfFirstTxn } from "@/lib/referralStore";
import { recordBooking } from "@/lib/analyticsStore";
import { mirror } from "@/lib/syncStore";
import { SUPABASE_TABLES } from "@/lib/supabaseClient";

const fireMirror = (table, row) => { try { mirror(table, row); } catch {} };

const BUS_EVT = "neyra-booking-change";
const emit = () => window.dispatchEvent(new Event(BUS_EVT));

export const subscribeBookings = (cb) => {
    window.addEventListener(BUS_EVT, cb);
    return () => window.removeEventListener(BUS_EVT, cb);
};

const read = (k, fallback) => {
    try {
        const v = localStorage.getItem(k);
        return v ? JSON.parse(v) : fallback;
    } catch {
        return fallback;
    }
};
const write = (k, v) => {
    localStorage.setItem(k, JSON.stringify(v));
    emit();
};

/* ---------------- Global bookings index ---------------- */

const ALL_KEY = "neyra.bookings.all";

const getAll = () => {
    const list = read(ALL_KEY, []);
    return Array.isArray(list) ? list : [];
};
const saveAll = (list) => write(ALL_KEY, list);

export const getBookingById = (id) =>
    getAll().find((b) => b.id === id) || null;

export const getBookingsByBuyer = (userId) =>
    getAll().filter((b) => b.buyerId === userId).sort((a, b) => b.createdAt - a.createdAt);

export const getBookingsBySeller = (userId) =>
    getAll().filter((b) => b.sellerId === userId).sort((a, b) => b.createdAt - a.createdAt);

/** Create booking after successful payment. Parks funds in seller's pending. */
export const createBooking = ({
    service,
    buyer,
    seller,
    date,
    time,
    location,
    mode, // "in_person" | "virtual"
    amount,
    paymentRef,
    paymentMethod,
}) => {
    const now = Date.now();
    const id = "bk_" + now.toString(36) + Math.random().toString(36).slice(2, 6);
    const booking = {
        id,
        serviceId: service?.id || null,
        serviceName: service?.name || "Service booking",
        serviceImage: service?.image || null,
        serviceCategory: service?.category || null,
        buyerId: buyer?.id || null,
        buyerName: buyer?.fullName || buyer?.name || "Customer",
        buyerUsername: buyer?.username || null,
        sellerId: seller?.id || null,
        sellerName: seller?.fullName || seller?.name || "Provider",
        sellerUsername: seller?.username || null,
        date: date || null,
        time: time || null,
        mode: mode || "in_person",
        location: location || null,
        amount: Number(amount) || 0,
        paymentRef: paymentRef || null,
        paymentMethod: paymentMethod || null,
        status: "pending", // pending | completed | released | disputed | cancelled
        createdAt: now,
        updatedAt: now,
        review: null,      // { rating, comment, at }
        dispute: null,     // { reason, detail, at, status: "open"|"resolved" }
    };
    const list = getAll();
    list.unshift(booking);
    saveAll(list);
    fireMirror(SUPABASE_TABLES.BOOKINGS, booking);

    // Park funds in seller's pending (escrow).
    if (booking.sellerId && booking.amount > 0) {
        addPendingFunds(booking.sellerId, booking.amount, {
            kind: "escrow_in",
            title: `Booking — ${booking.serviceName}`,
            escrowId: booking.id,
        });
    }

    // Notifications for both parties.
    try {
        if (booking.sellerId) {
            addNotification(booking.sellerId, {
                type: "payment",
                title: "New booking request",
                body: `${booking.buyerName} booked ${booking.serviceName} for ${booking.date} at ${booking.time}.`,
                link: `/bookings/${booking.id}`,
            });
        }
        if (booking.buyerId) {
            addNotification(booking.buyerId, {
                type: "payment",
                title: "Booking confirmed",
                body: `Your payment of ₦${booking.amount.toLocaleString()} is held in escrow until completion.`,
                link: `/bookings/${booking.id}`,
            });
        }
    } catch {}

    // Analytics: service-level booking count.
    if (booking.serviceId) {
        try { recordBooking("service", booking.serviceId); } catch {}
    }

    // Referral reward: buyer's first transaction triggers a reward to whoever
    // invited them (if any) via the referralStore.
    if (booking.buyerId) {
        try { rewardIfFirstTxn(booking.buyerId, { addRefundFn: addRefund, addNotificationFn: addNotification }); } catch {}
    }

    return booking;
};

/** Buyer confirms completion → release funds from pending to seller's available. */
export const confirmBookingCompletion = (bookingId) => {
    const list = getAll();
    const idx = list.findIndex((b) => b.id === bookingId);
    if (idx < 0) return { ok: false, error: "Booking not found" };
    const b = list[idx];
    if (b.status !== "pending") {
        return { ok: false, error: `Booking is already ${b.status}` };
    }
    list[idx] = { ...b, status: "released", updatedAt: Date.now(), releasedAt: Date.now() };
    saveAll(list);
    fireMirror(SUPABASE_TABLES.BOOKINGS, list[idx]);

    if (b.sellerId && b.amount > 0) {
        releaseFunds(b.sellerId, b.amount, {
            title: `Released — ${b.serviceName}`,
            escrowId: b.id,
        });
    }

    // Notify seller that funds were released.
    try {
        if (b.sellerId) {
            addNotification(b.sellerId, {
                type: "payment",
                title: "Funds released",
                body: `${b.buyerName} confirmed completion. ₦${b.amount.toLocaleString()} moved to available balance.`,
                link: `/bookings/${b.id}`,
            });
        }
    } catch {}

    return { ok: true, booking: list[idx] };
};

/** Buyer files a dispute → status = disputed, funds stay in escrow (no release). */
export const disputeBooking = (bookingId, { reason, detail }) => {
    const list = getAll();
    const idx = list.findIndex((b) => b.id === bookingId);
    if (idx < 0) return { ok: false, error: "Booking not found" };
    const b = list[idx];
    if (b.status === "released" || b.status === "cancelled") {
        return { ok: false, error: `Can't dispute a ${b.status} booking` };
    }
    list[idx] = {
        ...b,
        status: "disputed",
        updatedAt: Date.now(),
        dispute: {
            reason: reason || "Other",
            detail: (detail || "").trim() || null,
            at: Date.now(),
            status: "open",
        },
    };
    saveAll(list);
    fireMirror(SUPABASE_TABLES.BOOKINGS, list[idx]);
    fireMirror(SUPABASE_TABLES.REPORTS, {
        id: "rp_" + b.id,
        bookingId: b.id,
        reporterId: b.buyerId,
        targetId: b.sellerId,
        reason: reason || "Other",
        detail: (detail || "").trim() || null,
        status: "open",
        createdAt: Date.now(),
    });
    return { ok: true, booking: list[idx] };
};

/** Seller or buyer can cancel before completion (refund path — simplified). */
export const cancelBooking = (bookingId) => {
    const list = getAll();
    const idx = list.findIndex((b) => b.id === bookingId);
    if (idx < 0) return { ok: false, error: "Booking not found" };
    const b = list[idx];
    if (b.status === "released") return { ok: false, error: "Already released" };

    // If funds are in seller's pending, reverse them into buyer's available (refund).
    if (b.status === "pending" && b.sellerId && b.amount > 0) {
        // Drain seller pending by registering a refund credit on buyer side
        // and an escrow "return" on seller side (debit transaction).
        addRefund(b.buyerId, b.amount, {
            title: `Refund — ${b.serviceName}`,
        });
    }
    list[idx] = { ...b, status: "cancelled", updatedAt: Date.now() };
    saveAll(list);
    fireMirror(SUPABASE_TABLES.BOOKINGS, list[idx]);
    return { ok: true, booking: list[idx] };
};

/* ---------------- Reviews ---------------- */

/**
 * Attaches review to a booking AND appends to seller's review list so it shows
 * on the business profile.
 */
export const submitReview = (bookingId, { rating, comment }) => {
    const r = Math.max(1, Math.min(5, Number(rating) || 0));
    if (!r) return { ok: false, error: "Please pick a rating" };

    const list = getAll();
    const idx = list.findIndex((b) => b.id === bookingId);
    if (idx < 0) return { ok: false, error: "Booking not found" };
    const b = list[idx];
    if (b.review) return { ok: false, error: "Review already submitted" };

    const review = {
        id: "rv_" + Date.now().toString(36),
        bookingId,
        rating: r,
        comment: (comment || "").trim() || null,
        at: Date.now(),
        buyerId: b.buyerId,
        buyerName: b.buyerName,
        buyerUsername: b.buyerUsername,
        serviceId: b.serviceId,
        serviceName: b.serviceName,
    };

    list[idx] = { ...b, review, updatedAt: Date.now() };
    saveAll(list);
    fireMirror(SUPABASE_TABLES.REVIEWS, review);
    fireMirror(SUPABASE_TABLES.BOOKINGS, list[idx]);

    // Also index by seller for profile tab.
    if (b.sellerId) {
        const k = `neyra.reviews.${b.sellerId}`;
        const arr = read(k, []);
        arr.unshift(review);
        write(k, arr);

        try {
            addNotification(b.sellerId, {
                type: "review",
                title: `${r}-star review`,
                body: review.comment
                    ? `${b.buyerName}: "${review.comment.slice(0, 80)}"`
                    : `${b.buyerName} left you a ${r}-star review.`,
                link: `/bookings/${b.id}`,
            });
        } catch {}
    }
    return { ok: true, review };
};

export const getReviewsForSeller = (sellerId) =>
    read(`neyra.reviews.${sellerId}`, []);

export const getReviewsForService = (serviceId) =>
    getAll()
        .filter((b) => b.serviceId === serviceId && b.review)
        .map((b) => b.review)
        .sort((a, b) => b.at - a.at);

export const getSellerRatingSummary = (sellerId) => {
    const arr = getReviewsForSeller(sellerId);
    if (arr.length === 0) return { count: 0, average: 0 };
    const sum = arr.reduce((acc, r) => acc + r.rating, 0);
    return { count: arr.length, average: Math.round((sum / arr.length) * 10) / 10 };
};

/* ---------------- Dispute reasons (preset) ---------------- */

export const DISPUTE_REASONS = [
    { id: "not_delivered", label: "Service not delivered" },
    { id: "quality",       label: "Quality was poor" },
    { id: "no_show",       label: "Provider didn't show up" },
    { id: "misleading",    label: "Service was different from listing" },
    { id: "payment",       label: "Payment or refund issue" },
    { id: "behaviour",     label: "Unprofessional behaviour" },
    { id: "other",         label: "Other" },
];
